import { useEffect, useState } from 'react'
import { Check, Copy, Link2, Trash2 } from 'lucide-react'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  useCreateShare,
  useRevokeShare,
  useThreadShares,
} from './queries'

interface Props {
  open: boolean
  threadId: string | null
  onClose: () => void
}

function shortId(id: string): string {
  return id.slice(0, 8) + '…'
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.round(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.round(hrs / 24)
  return `${days}d ago`
}

export function ShareLinkDialog({ open, threadId, onClose }: Props) {
  const sharesQ = useThreadShares(open ? threadId : null)
  const createM = useCreateShare()
  const revokeM = useRevokeShare()
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [revokeTargetId, setRevokeTargetId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setCopiedId(null)
      setError(null)
      setRevokeTargetId(null)
    }
  }, [open])

  const shares = sharesQ.data?.shares ?? []
  const active = shares.filter((s) => !s.revoked_at)

  const create = async () => {
    if (!threadId) return
    setError(null)
    try {
      await createM.mutateAsync({ threadId })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create link.')
    }
  }

  const copy = async (url: string, id: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiedId(id)
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1800)
    } catch {
      setError('Copy failed - the link is visible below.')
    }
  }

  const performRevoke = async () => {
    if (!threadId || !revokeTargetId) return
    const shareId = revokeTargetId
    try {
      await revokeM.mutateAsync({ shareId, threadId })
    } finally {
      setRevokeTargetId(null)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(o) => (o ? null : onClose())}
      title="Share this chat"
      description="Anyone with the link can read the conversation. No Bokapsys login required - revoke any time."
    >
      <div className="space-y-3">
        <Button
          type="button"
          className="gap-2"
          onClick={create}
          disabled={createM.isPending}
        >
          <Link2 className="size-3.5" />
          {createM.isPending ? 'Creating…' : 'Create a new share link'}
        </Button>
        {error ? <div className="text-xs text-destructive">{error}</div> : null}

        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Active links ({active.length})
        </div>
        {sharesQ.isLoading ? (
          <div className="py-2 text-xs text-muted-foreground">Loading…</div>
        ) : active.length === 0 ? (
          <div className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
            No active share links for this thread yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {active.map((s) => (
              <li key={s.id} className="flex items-center gap-2 rounded-md border border-border bg-card p-2">
                <div className="min-w-0 flex-1">
                  <Input
                    value={s.url || ''}
                    readOnly
                    className="h-8 font-mono text-[11px]"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    {shortId(s.id)} · created {relativeTime(s.created_at)}
                    {s.expires_at ? ` · expires ${new Date(s.expires_at).toLocaleDateString()}` : ''}
                  </div>
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="size-8"
                  onClick={() => s.url && copy(s.url, s.id)}
                  disabled={!s.url}
                  title="Copy link"
                  aria-label="Copy link"
                >
                  {copiedId === s.id ? <Check className="size-3.5 text-primary" /> : <Copy className="size-3.5" />}
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="size-8 text-destructive hover:text-destructive"
                  onClick={() => setRevokeTargetId(s.id)}
                  disabled={revokeM.isPending}
                  title="Revoke link"
                  aria-label="Revoke link"
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Modal.Confirm
        open={!!revokeTargetId}
        onOpenChange={(o) => (o ? null : setRevokeTargetId(null))}
        title="Revoke this share link?"
        body="Anyone holding the URL will lose access immediately."
        confirmLabel="Revoke"
        confirmVariant="danger"
        onConfirm={performRevoke}
        busy={revokeM.isPending}
      />
    </Modal>
  )
}
